import {
    Component,
    OnInit,
    OnDestroy,
    signal,
    inject,
    DestroyRef
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import {
    changei18n,
    GridResourceStringsEN
} from '@infragistics/igniteui-angular';
import { interval, Subscription } from 'rxjs';

import { i18nDE } from './core/utils/language-resources/ignite-ui-grid.de';
import { AuthService } from './services/auth.service';
import { SyncServiceNew } from './services/sync-new.service';
import { OnlineStatusService } from './services/online-status.service';
import { concatMap } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { ToastrService } from 'ngx-toastr';
import { SwUpdate } from '@angular/service-worker';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [RouterOutlet]
})
export class AppComponent implements OnInit, OnDestroy {
    private authService = inject(AuthService);
    private syncServiceNew = inject(SyncServiceNew);
    private onlineStatusService = inject(OnlineStatusService);
    private toastrService = inject(ToastrService);
    private swUpdate = inject(SwUpdate);
    private destroyRef = inject(DestroyRef);

    canUseSignals = signal(true);
    isLoading = false;

    private onlineSub: Subscription;
    private forceFullSyncSub: Subscription;

    private bookingsWorker: Worker;
    private customersWorker: Worker;

    constructor() {
        this.bookingsWorker = new Worker(
            new URL('./core/workers/bookings.worker', import.meta.url),
            {
                type: 'module'
            }
        );

        this.customersWorker = new Worker(
            new URL('./core/workers/customers.worker', import.meta.url),
            {
                type: 'module'
            }
        );

        if (this.swUpdate.isEnabled) {
            this.swUpdate.versionUpdates
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(event => {
                    if (event.type === 'VERSION_READY') {
                        this.applyAppUpdate();
                    }
                });
        }
    }

    ngOnInit(): void {
        const currentRS = GridResourceStringsEN;
        const onlineInterval = interval(10000);

        // Check if full sync is needed every hour 1000 * 60 * 60
        const forceFullSyncInterval = interval(1000 * 60);

        this.bookingsWorker.onmessage = ({ data }) => {
            if (typeof data === 'string') {
                switch (data as RequestKey) {
                    case 'SHOW_TOAST':
                        this.toastrService.info(
                            'Ältere Buchungen werden im Hintergrund nachgeladen',
                            'Hintergrundprozess gestartet'
                        );
                        const userInfo = JSON.parse(
                            localStorage.getItem('thumedi.security.userInfo')
                        );
                        const request = {
                            userInfo,
                            baseApiUrl: environment.baseUrl,
                            urlComplement:
                                '/api/v2/bookings/get-updated?isFirstLoad=true&onlyTwoYears=false'
                        };

                        this.bookingsWorker.postMessage({
                            key: 'PROCESS',
                            data: request
                        });
                        break;
                    case 'ERROR':
                        this.toastrService.warning(
                            'Beim bearbeiten der älteren Buchungen trat ein unerwarteter Fehler auf',
                            'Fehler'
                        );
                        break;
                    default:
                        console.log(
                            'An unexpected value was send from the worker' +
                                data
                        );
                        break;
                }

                return;
            }

            if (typeof data === 'object') {
                const { success, message } = data;

                if (success === true) {
                    this.toastrService.success(
                        'Synchronisierung abgeschlossen',
                        'Hintergrundprozess abgeschlossen'
                    );
                } else {
                    switch (message as ResultKey) {
                        case 'API_ERROR':
                            this.toastrService.error(
                                'Beim bearbeiten der älteren Buchungen trat ein unerwarteter Fehler auf',
                                'Fehler'
                            );
                            break;
                        case 'INDEXED_DB_ERROR':
                            this.toastrService.error(
                                'An error ocurred while inserting old bookings',
                                'Background process error'
                            );
                            break;
                        case 'NO_DATA':
                            console.warn(
                                'No records were found',
                                'Background process warning'
                            );
                            break;
                        case 'USER_ERROR':
                            console.warn(
                                'Unable to get user info',
                                'Background process error'
                            );
                    }
                }
            }
        };

        this.customersWorker.onmessage = ({ data }) => {
            if (typeof data === 'string') {
                switch (data as RequestKey) {
                    case 'SHOW_TOAST':
                        const userInfo = JSON.parse(
                            localStorage.getItem('thumedi.security.userInfo')
                        );

                        if (userInfo.isAdmin) {
                            this.toastrService.info(
                                'Synchronisieren inaktiver Kunden',
                                'Hintergrundprozess gestartet'
                            );

                            const request = {
                                userInfo,
                                baseApiUrl: environment.baseUrl,
                                urlComplement:
                                    '/api/v2/customers/get-updated?isFirstLoad=true&onlyActive=false'
                            };

                            this.customersWorker.postMessage({
                                key: 'PROCESS',
                                data: request
                            });
                        }
                        break;
                    case 'ERROR':
                        this.toastrService.warning(
                            'Beim bearbeiten der älteren Buchungen trat ein unerwarteter Fehler auf',
                            'Fehler'
                        );
                        break;
                    default:
                        console.log(
                            'An unexpected value was send from the worker' +
                                data
                        );
                        break;
                }

                return;
            }

            if (typeof data === 'object') {
                const { success, message } = data;

                if (success === true) {
                    this.toastrService.success(
                        'Kundensynchronisierung abgeschlossen',
                        'Hintergrundprozess abgeschlossen'
                    );
                } else {
                    switch (message as ResultKey) {
                        case 'API_ERROR':
                            this.toastrService.error(
                                'Beim Synchronisieren der inaktiven Kunden ist ein Fehler aufgetreten',
                                'Fehler'
                            );
                            break;
                        case 'INDEXED_DB_ERROR':
                            this.toastrService.error(
                                'Beim Einfügen der inaktiven Kunden ist ein Fehler aufgetreten',
                                'Fehler'
                            );
                            break;
                        case 'NO_DATA':
                            console.warn(
                                'No records were found',
                                'Background process warning'
                            );
                            break;
                        case 'USER_ERROR':
                            console.warn(
                                'Unable to get user info',
                                'Background process error'
                            );
                    }
                }
            }
        };

        this.syncServiceNew.start(this.bookingsWorker, this.customersWorker);
        Object.keys(currentRS).forEach(k => (currentRS[k] = i18nDE[k]));
        changei18n(currentRS);

        this.onlineSub = onlineInterval
            .pipe(concatMap(() => this.onlineStatusService.checkOnlineStatus()))
            .subscribe();

        this.forceFullSyncSub = forceFullSyncInterval
            .pipe(concatMap(() => this.syncServiceNew.checkFullSyncNeeded()))
            .subscribe();

        if (this.authService.isLoggedIn()) {
            this.syncServiceNew.validateSyncRequirement();
            this.syncServiceNew.sync(); // Force sync on every app load to account for operations done after login
        }
    }

    ngOnDestroy(): void {
        this.onlineSub.unsubscribe();
        this.forceFullSyncSub.unsubscribe();
        this.bookingsWorker.terminate();
        this.customersWorker.terminate();
    }

    private async applyAppUpdate(): Promise<void> {
        try {
            const activated = await this.swUpdate.activateUpdate();
            if (activated) {
                window.location.reload();
            }
        } catch (error) {
            console.error('Error while updating the app', error);
            // Optionally reload anyway to ensure user gets the update
            window.location.reload();
        }
    }
}

type RequestKey = 'SHOW_TOAST' | 'ERROR';
type ResultKey = 'USER_ERROR' | 'API_ERROR' | 'INDEXED_DB_ERROR' | 'NO_DATA';
